import { ButtonBase, makeStyles } from '@material-ui/core';
import { DescriptionOutlined, OutlinedFlag, ReportProblemOutlined } from '@material-ui/icons';
import { ToggleButton } from '@material-ui/lab';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { TextField } from '@roc/ui';
import { formatCurrency } from '@roc/ui/utils';
import {
  PostQcReviewStore,
  QcField,
  QcFieldRef,
  QcFilter,
  QcSection,
  fieldKey,
  isBlank,
} from './postQcReviewStore';
import { qcEyebrow, qcHiddenScrollbar, qcPalette } from './postQcPalette';

const GRID_COLUMNS = '34% minmax(0, 1fr) 128px';

const EMPTY_FILTER_MESSAGE: Record<QcFilter, string> = {
  ALL: 'No documents to review.',
  NEEDS_REVISION: 'No fields are flagged for revision.',
};

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

const useStyles = makeStyles(theme => ({
  table: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minWidth: 0,
  },
  columns: {
    ...qcEyebrow,
    display: 'grid',
    gridTemplateColumns: GRID_COLUMNS,
    padding: theme.spacing(1.25, 3),
    backgroundColor: qcPalette.subtle,
    borderBottom: `1px solid ${qcPalette.border}`,
  },
  rows: {
    ...qcHiddenScrollbar,
    flex: 1,
    overflowY: 'auto',
  },
  group: {
    ...qcEyebrow,
    padding: theme.spacing(2, 3, 1),
    fontSize: 16,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    width: '100%',
    padding: theme.spacing(1.25, 3),
    boxSizing: 'border-box',
    textAlign: 'left',
    backgroundColor: theme.palette.common.white,
    borderTop: `1px solid ${qcPalette.border}`,
    borderBottom: `1px solid ${qcPalette.sunken}`,
    '&:hover': {
      backgroundColor: qcPalette.subtle,
    },
  },
  sectionHeaderSelected: {
    backgroundColor: qcPalette.verifiedSoft,
    boxShadow: `inset 3px 0 0 ${qcPalette.navy}`,
    '&:hover': {
      backgroundColor: qcPalette.verifiedSoft,
    },
  },
  sectionIcon: {
    width: 32,
    height: 32,
    flexShrink: 0,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: qcPalette.sunken,
    color: qcPalette.inkSoft,
  },
  sectionIconSelected: {
    backgroundColor: qcPalette.navy,
    color: theme.palette.common.white,
  },
  sectionText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
    minWidth: 0,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: qcPalette.ink,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  sectionSubtitle: {
    fontSize: 12,
    color: qcPalette.muted,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  sectionMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexShrink: 0,
  },
  sectionCount: {
    fontSize: 12,
    color: qcPalette.muted,
    whiteSpace: 'nowrap',
  },
  flaggedBadge: {
    height: 20,
    padding: '0 8px',
    borderRadius: 10,
    backgroundColor: qcPalette.flaggedSoft,
    color: qcPalette.flaggedInk,
    fontSize: 11,
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
  },
  notice: {
    margin: theme.spacing(1, 3),
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    backgroundColor: qcPalette.mismatchSoft,
    color: qcPalette.mismatchInk,
    fontSize: 13,
  },
  empty: {
    padding: theme.spacing(6, 3),
    textAlign: 'center',
    color: qcPalette.muted,
    fontSize: 14,
  },
  row: {
    borderBottom: `1px solid ${qcPalette.sunken}`,
    cursor: 'pointer',
    outline: 'none',
  },
  rowFlagged: {
    boxShadow: `inset 3px 0 0 ${qcPalette.flagged}`,
  },
  rowFocused: {
    boxShadow: `inset 0 0 0 2px ${qcPalette.verifiedLine}`,
  },
  rowFlaggedFocused: {
    boxShadow: `inset 3px 0 0 ${qcPalette.flagged}, inset 0 0 0 2px ${qcPalette.verifiedLine}`,
  },
  rowGrid: {
    padding: theme.spacing(1.75, 3),
    display: 'grid',
    gridTemplateColumns: GRID_COLUMNS,
    alignItems: 'start',
  },
  labelCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    paddingRight: theme.spacing(2),
  },
  label: {
    fontSize: 13,
    lineHeight: '20px',
    fontWeight: 600,
    color: qcPalette.ink,
  },
  value: {
    fontSize: 13,
    lineHeight: '20px',
    paddingRight: theme.spacing(2),
    wordBreak: 'break-word',
    color: qcPalette.ink,
  },
  flagCell: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  flag: {
    height: 36,
    padding: '0 14px',
    borderRadius: 10,
    border: `1px solid ${qcPalette.borderStrong}`,
    backgroundColor: theme.palette.common.white,
    textTransform: 'none',
    fontSize: 12,
    fontWeight: 600,
    color: qcPalette.inkSoft,
    '& svg': {
      fontSize: 16,
      marginRight: 6,
    },
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: qcPalette.flagged,
      borderColor: qcPalette.flagged,
      color: theme.palette.common.white,
    },
  },
  flagText: {
    fontSize: 12,
    fontWeight: 600,
    color: qcPalette.flaggedInk,
  },
  noteRow: {
    display: 'grid',
    gridTemplateColumns: '34% minmax(0, 1fr)',
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.75, 3),
  },
  noteLine: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  bubble: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1.25, 1.75),
    borderRadius: 14,
    borderTopLeftRadius: 4,
    border: `1px solid ${qcPalette.flaggedLine}`,
    backgroundColor: theme.palette.common.white,
  },
  bubbleEditing: {
    borderColor: qcPalette.verified,
    boxShadow: `0 0 0 3px ${qcPalette.verifiedSoft}`,
  },
  bubbleInput: {
    '& .MuiInputBase-root': {
      padding: 0,
      fontSize: 13,
      lineHeight: '20px',
      color: qcPalette.ink,
    },
    '& textarea': {
      padding: 0,
    },
  },
  bubbleText: {
    margin: 0,
    fontSize: 13,
    lineHeight: '20px',
    color: qcPalette.ink,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  bubbleEmpty: {
    fontSize: 13,
    fontStyle: 'italic',
    color: qcPalette.muted,
  },
  bubbleActions: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  bubbleLink: {
    flexShrink: 0,
    padding: '2px 6px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    color: qcPalette.verified,
    '&:hover': {
      backgroundColor: qcPalette.verifiedSoft,
    },
    '&.Mui-disabled': {
      color: qcPalette.muted,
    },
  },
  note: {
    flex: 1,
    minWidth: 0,
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.common.white,
      borderRadius: 10,
      fontSize: 13,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: qcPalette.flaggedLine,
    },
  },
}));

const formatValue = (value: string | number | null) => {
  if (isBlank(value)) {
    return '—';
  }
  return typeof value === 'number' ? formatCurrency(value) : String(value);
};

const sectionSubtitle = (section: QcSection) => {
  if (section.documents.length === 0) {
    return 'No file linked';
  }
  if (section.documents.length === 1) {
    return section.documents[0].fileName;
  }
  return `${section.documents.length} files`;
};

type SectionHeaderProps = {
  store: PostQcReviewStore;
  section: QcSection;
};

const SectionHeader = observer(({ store, section }: SectionHeaderProps) => {
  const classes = useStyles();
  const counts = store.sectionCounts(section);
  const selected = store.expandedSection === section.sectionName;

  return (
    <ButtonBase
      className={clsx(classes.sectionHeader, selected && classes.sectionHeaderSelected)}
      aria-current={selected ? 'true' : undefined}
      onClick={() => store.selectSection(section.sectionName)}
    >
      <span className={clsx(classes.sectionIcon, selected && classes.sectionIconSelected)}>
        <DescriptionOutlined fontSize="small" />
      </span>
      <span className={classes.sectionText}>
        <span className={classes.sectionTitle} title={section.sectionName}>{section.sectionName}</span>
        <span className={classes.sectionSubtitle}>{sectionSubtitle(section)}</span>
      </span>
      <span className={classes.sectionMeta}>
        {counts.flagged > 0 && <span className={classes.flaggedBadge}>{counts.flagged} flagged</span>}
        <span className={classes.sectionCount}>{plural(counts.total, 'field')}</span>
      </span>
    </ButtonBase>
  );
});

type FieldRowProps = {
  store: PostQcReviewStore;
  section: QcSection;
  field: QcField;
  readOnly: boolean;
  rowRef: (element: HTMLElement) => void;
  commentRef: (element: HTMLTextAreaElement) => void;
};

const FieldRow = observer(({ store, section, field, readOnly, rowRef, commentRef }: FieldRowProps) => {
  const classes = useStyles();
  const ref: QcFieldRef = { sectionName: section.sectionName, itemName: field.itemName };
  const flagged = field.sendForRevision;
  const focused = store.isFocused(ref);
  const commentMissing = flagged && isBlank(field.reviewerComment);
  const editing = flagged && !readOnly && (store.isEditingNote(ref) || commentMissing);
  const editingPosted = editing && store.hasPostedNote(ref);
  const [focusNote, setFocusNote] = useState(false);
  const noteInput = useRef<HTMLTextAreaElement>(null);
  const commentId = `post-qc-note-${fieldKey(ref)}`.replace(/[^A-Za-z0-9_-]/g, '-');

  useEffect(() => {
    if (editing && focusNote && noteInput.current) {
      const input = noteInput.current;
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
      setFocusNote(false);
    }
  }, [editing, focusNote]);

  const noteRef = (element: HTMLTextAreaElement) => {
    noteInput.current = element;
    commentRef(element);
  };

  const onNoteKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      event.preventDefault();
      if (editingPosted) {
        store.cancelNoteEdit(ref);
      } else {
        (event.target as HTMLElement).blur();
      }
    }
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && !(commentMissing && !editingPosted)) {
      event.preventDefault();
      store.saveNote(ref);
    }
  };

  return (
    <div
      ref={rowRef}
      className={clsx(
        classes.row,
        flagged && classes.rowFlagged,
        focused && (flagged ? classes.rowFlaggedFocused : classes.rowFocused)
      )}
      onClick={() => store.focusField(ref)}
    > 
      <div className={classes.rowGrid}>
        <div className={classes.labelCell}>
          <span className={classes.label}>{field.itemDisplayName}</span>
        </div>
        <span className={classes.value}>{formatValue(field.expectedData)}</span>
        <div className={classes.flagCell}>
          {readOnly ? (
            flagged && <span className={classes.flagText}>Flagged</span>
          ) : (
            <ToggleButton
              value="flag"
              size="small"
              selected={flagged}
              aria-label={`Flag ${field.itemDisplayName} for revision`}
              className={classes.flag}
              onChange={() => store.toggleFlag(ref)}
            >
              <OutlinedFlag />
              {flagged ? 'Flagged' : 'Flag'}
            </ToggleButton>
          )}
        </div>
      </div>
      {flagged && (
        <div className={classes.noteRow} onClick={event => event.stopPropagation()}>
          <span />
          <div>
            {editing && !editingPosted && (
              <div className={classes.noteLine}>
                <TextField
                  id={commentId}
                  testId="post-qc-comment"
                  variant="outlined"
                  standaloneLabel
                  multiline
                  minRows={1}
                  maxRows={6}
                  fullWidth
                  className={classes.note}
                  placeholder="Write a revision note for the closing team…"
                  inputProps={{ 'aria-label': `Revision note for ${field.itemDisplayName}` }}
                  value={field.reviewerComment ?? ''}
                  inputRef={noteRef}
                  onKeyDown={onNoteKeyDown}
                  onChange={event => store.setComment(ref, event.target.value)}
                />
              </div>
            )}
            {(!editing || editingPosted) && (
              <div className={clsx(classes.bubble, editingPosted && classes.bubbleEditing)}>
                {editingPosted ? (
                  <TextField
                    id={commentId}
                    testId="post-qc-comment"
                    variant="standard"
                    standaloneLabel
                    multiline
                    minRows={1}
                    maxRows={8}
                    fullWidth
                    className={classes.bubbleInput}
                    InputProps={{ disableUnderline: true }}
                    inputProps={{ 'aria-label': `Revision note for ${field.itemDisplayName}` }}
                    value={field.reviewerComment ?? ''}
                    inputRef={noteRef}
                    onKeyDown={onNoteKeyDown}
                    onChange={event => store.setComment(ref, event.target.value)}
                  />
                ) : isBlank(field.reviewerComment) ? (
                  <span className={classes.bubbleEmpty}>No note</span>
                ) : (
                  <p className={classes.bubbleText}>{field.reviewerComment}</p>
                )}
                {!readOnly && (
                  <div className={classes.bubbleActions}>
                    {editingPosted ? (
                      <ButtonBase
                        className={classes.bubbleLink}
                        aria-label={`Save note for ${field.itemDisplayName}`}
                        disabled={store.saving}
                        onClick={() => store.saveNote(ref)}
                      >
                        Save
                      </ButtonBase>
                    ) : (
                      <ButtonBase
                        className={classes.bubbleLink}
                        aria-label={`Edit note for ${field.itemDisplayName}`}
                        onClick={() => {
                          setFocusNote(true);
                          store.editNote(ref);
                        }}
                      >
                        Edit
                      </ButtonBase>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

type PostQcFieldTableProps = {
  store: PostQcReviewStore;
  readOnly: boolean;
  keyboardEnabled: boolean;
};

export const PostQcFieldTable = observer(({ store, readOnly, keyboardEnabled }: PostQcFieldTableProps) => {
  const classes = useStyles();
  const rows = useRef(new Map<string, HTMLElement>());
  const comments = useRef(new Map<string, HTMLTextAreaElement>());
  const pendingCommentFocus = useRef<string>(null);
  const focusedField = store.focusedField;

  useEffect(() => {
    if (!focusedField || pendingCommentFocus.current !== fieldKey(focusedField)) {
      pendingCommentFocus.current = null;
    }
    if (!focusedField) {
      return;
    }
    const frame = window.requestAnimationFrame(() =>
      rows.current.get(fieldKey(focusedField))?.scrollIntoView({ block: 'nearest' })
    );
    return () => window.cancelAnimationFrame(frame);
  }, [focusedField]);

  useEffect(() => {
    if (!keyboardEnabled) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) {
        return;
      }
      const target = event.target as HTMLElement;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      if (target?.closest && target.closest('[role="listbox"],[role="menu"],[role="tablist"],[role="option"],[role="menuitem"]')) {
        return;
      }
      if (event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }
      const focused = store.focusedField;
      switch (event.key) {
        case 'ArrowDown':
          store.moveFocus(1);
          break;
        case 'ArrowUp':
          store.moveFocus(-1);
          break;
        case 'f':
        case 'F': {
          if (!focused || readOnly) {
            return;
          }
          const key = fieldKey(focused);
          store.toggleFlag(focused);
          const existing = comments.current.get(key);
          if (existing) {
            existing.focus();
          } else {
            pendingCommentFocus.current = key;
          }
          break;
        }
        default:
          return;
      }
      event.preventDefault();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [keyboardEnabled, readOnly, store]);

  const registerRow = (key: string) => (element: HTMLElement) => {
    if (element) {
      rows.current.set(key, element);
    } else {
      rows.current.delete(key);
    }
  };

  const registerComment = (key: string) => (element: HTMLTextAreaElement) => {
    if (element) {
      comments.current.set(key, element);
      if (pendingCommentFocus.current === key) {
        pendingCommentFocus.current = null;
        element.focus();
      }
    } else {
      comments.current.delete(key);
    }
  };

  return (
    <div className={classes.table}>
      <div className={classes.rows}>
        {store.visibleGroups.length === 0 && (
          <div className={classes.empty}>{EMPTY_FILTER_MESSAGE[store.filter]}</div>
        )}
        {store.visibleGroups.map(group => (
          <section key={group.group} aria-label={group.group}>
            <div className={classes.group}>{group.group}</div>
            <div className={classes.columns}>
              <span>Field</span>
              <span>Portal value</span>
              <span style={{ textAlign: 'right' }}>Revision</span>
            </div>
            {group.sections.map(section => {
              const items = store.visibleItems(section);
              return (
                <div key={section.sectionName}>
                  <SectionHeader store={store} section={section} />
                  {section.status === 'IN_PROGRESS' && (
                    <div className={classes.notice}>Extraction is still running. You can review these fields against the document.</div>
                  )}
                  {section.status === 'FAILED' && (
                    <div className={classes.notice}>{section.errorMessage || 'Extraction failed for this document.'}</div>
                  )}
                  {items.length === 0 && (
                    <div className={classes.empty}>
                      {section.items.length === 0 ? 'No fields to review for this document.' : 'No fields in this document match the current filter.'}
                    </div>
                  )}
                  {items.map(field => {
                    const key = fieldKey({ sectionName: section.sectionName, itemName: field.itemName });
                    return (
                      <FieldRow
                        key={field.itemName}
                        store={store}
                        section={section}
                        field={field}
                        readOnly={readOnly}
                        rowRef={registerRow(key)}
                        commentRef={registerComment(key)}
                      />
                    );
                  })}
                </div>
              );
            })}
          </section>
        ))}
      </div>
    </div>
  );
});
